import { defineStore } from 'pinia'
import { logger } from '@/utils/logger'

interface VersionData {
    timestamp: string
    version: string
}

const POLLING_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

export const useVersionStore = defineStore('version', {
    state: () => ({
        isUpdateAvailable: false,
        loadedVersion: null as string | null,
        pollingInterval: null as ReturnType<typeof setInterval> | null,
        isPolling: false,
    }),
    getters: {
        hasUpdate(): boolean {
            return this.isUpdateAvailable
        },
    },
    actions: {
        async checkVersion(): Promise<void> {
            try {
                // Use cache-busting query param to bypass browser cache
                const response = await fetch(`/version.json?t=${Date.now()}`, {
                    cache: 'no-store',
                    headers: {
                        'Cache-Control': 'no-cache',
                    },
                })

                if (!response.ok) {
                    logger.warn('Failed to fetch version.json', { status: response.status })
                    return
                }

                const data: VersionData = await response.json()
                const serverVersion = data.timestamp || data.version

                if (!serverVersion) {
                    logger.warn('Invalid version.json format', data)
                    return
                }

                // First load: store the version
                if (this.loadedVersion === null) {
                    this.loadedVersion = serverVersion
                    logger.debug('Initial version loaded', { version: serverVersion })
                    return
                }

                // Compare versions
                if (serverVersion !== this.loadedVersion) {
                    logger.info('New version detected', {
                        current: this.loadedVersion,
                        new: serverVersion,
                    })
                    this.isUpdateAvailable = true
                    // Stop polling once update is detected
                    this.stopPolling()
                }
            } catch (error) {
                // Silently handle errors - network failures shouldn't break the app
                logger.debug('Version check failed (non-critical)', error)
            }
        },

        startPolling(): void {
            if (this.isPolling) {
                logger.debug('Version polling already started')
                return
            }

            this.isPolling = true

            // Initial check after a short delay
            setTimeout(() => {
                this.checkVersion()
            }, 1000)

            // Then poll every 5 minutes
            this.pollingInterval = setInterval(() => {
                this.checkVersion()
            }, POLLING_INTERVAL_MS)

            logger.debug('Version polling started')
        },

        stopPolling(): void {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval)
                this.pollingInterval = null
                this.isPolling = false
                logger.debug('Version polling stopped')
            }
        },
    },
})

